import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaClock,
  FaChartBar,
  FaBook,
  FaLightbulb,
  FaStar,
  FaTrophy,
  FaRedo,
  FaListAlt,
} from "react-icons/fa";  
import "./ResultPageMock.scss";
import { Container } from "react-bootstrap";

const ExamResult = () => {
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSection, setSelectedSection] = useState(null);
  const token = localStorage.getItem("token");
  const examId = localStorage.getItem("test_id");
  const user_id = localStorage.getItem("user_id");
  const location = useLocation();

  useEffect(() => {
    fetchExamResult();
  }, []);

  const fetchExamResult = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/user-result`,
        {
          modal_id: examId,
          user_id: user_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "1" && response.data.data) {
        setResultData(response.data.data);
        // Set first section as default selected
        if (
          response.data.data.sections &&
          response.data.data.sections.length > 0
        ) {
          setSelectedSection(response.data.data.sections[0]);
        }
      } else {
        setError(response.data.message || "Failed to fetch exam results");
      }
    } catch (err) {
      console.error("Error fetching exam result:", err);
      setError("An error occurred while fetching results");
    } finally {
      setLoading(false);
    }
  };

  // Safe number formatting functions
  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined) return "0";
    const num = parseFloat(value);
    return isNaN(num) ? "0" : num.toFixed(decimals);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "0";
    const num = parseFloat(value);
    return isNaN(num) ? "0" : num.toFixed(1);
  };

  const getPerformanceColor = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "#10b981";
    if (perc >= 60) return "#f59e0b";
    if (perc >= 40) return "#ef4444";
    return "#dc2626";
  };

  const getPerformanceIcon = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return <FaTrophy className="trophy-icon" />;
    if (perc >= 60) return <FaStar className="star-icon" />;
    return <FaLightbulb className="improve-icon" />;
  };

  const getPerformanceText = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "Excellent!";
    if (perc >= 60) return "Good Job!";
    if (perc >= 40) return "Needs Improvement";
    return "Keep Practicing";
  };

  // Get question range for section
  const getSectionQuestionRange = (sectionIndex) => {
    if (!resultData?.section_ranges?.[sectionIndex]) {
      return "1-1";
    }
    const range = resultData.section_ranges[sectionIndex];
    return `${range.start_question}-${range.end_question}`;
  };

  if (loading) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-loading">
          <div className="loading-spinner"></div>
          <h3>Analyzing Your Performance...</h3>
          <p>Preparing your detailed result report</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Results</h3>
          <p>{error}</p>
          <div className="error-actions">
            <Link to="/dashboard" className="btn primary">
              <FaArrowLeft className="me-2" />
              Back to Dashboard
            </Link>
            <button onClick={fetchExamResult} className="btn secondary">
              <FaRedo className="me-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-error">
          <h3>No Result Data Available</h3>
          <p>Please complete a test to view results</p>
          <Link to="/test-series" className="btn primary">
            Take a Test
          </Link>
        </div>
      </div>
    );
  }

  const {
    paper_details,
    overall_performance,
    sections,
    section_wise_summary,
    recommendations,
    section_ranges,
  } = resultData;

  // Safe defaults for overall_performance
  const safeOverall = overall_performance || {
    total_time_spent: 0,
    percentage: 0,
    obtained_marks: 0,
    total_marks: 0,
    correct_answers: 0,
    wrong_answers: 0,
    unanswered_questions: 0,
    average_time_per_question: 0,
    passing_status: "Failed",
  };

  return (
    <div className="exam-result-page">
      <div className="page_header"></div>
      <Container>
        <div className="result-container">
          {/* Header */}
          <div className="result-header my-3 shadow">
            <div className="header-content">
              <Link to="/test-series" className="back-btn">
                <FaArrowLeft className="me-2" />
                Back to Tests
              </Link>
              <div className="header-title">
                <h1>Exam Results</h1>
                <p className="paper-title">{paper_details?.paper_title}</p>
              </div>
              <div className="header-stats">
                <div className="stat-badge">
                  <FaClock className="me-1" />
                  {formatNumber(safeOverall.total_time_spent)} mins
                </div>
                <div
                  className={`result-badge ${safeOverall.passing_status?.toLowerCase()}`}
                >
                  {safeOverall.passing_status}
                </div>
              </div>
            </div>
          </div>
          {/* Main Result Cards */}
          <div className="result-summary-cards">
            {/* Overall Score Card */}
            <div className="summary-card overall-score">
              <div className="score-circle">
                <div
                  className="circle-progress"
                  style={{
                    background: `conic-gradient(${getPerformanceColor(
                      safeOverall.percentage
                    )} ${safeOverall.percentage * 3.6}deg, #e5e7eb 0deg)`,
                  }}
                >
                  <div className="circle-inner">
                    {getPerformanceIcon(safeOverall.percentage)}
                    <span className="score-percent">
                      {formatPercentage(safeOverall.percentage)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="score-details">
                <h3>
                  {getPerformanceText(
                    Number(safeOverall?.percentage || 0).toFixed(2)
                  )}
                </h3>
                <p>Overall Score</p>
                <div className="score-breakdown">
                  <span>
                    {Number(safeOverall?.obtained_marks || 0).toFixed(2)}/
                    {Number(safeOverall?.total_marks || 0)} Marks
                  </span>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card correct">
                <div className="stat-icon">
                  <FaCheck />
                </div>
                <div className="stat-info">
                  <span className="stat-number">
                    {safeOverall.correct_answers}
                  </span>
                  <span className="stat-label">Correct</span>
                </div>
              </div>

              <div className="stat-card incorrect">
                <div className="stat-icon">
                  <FaTimes />
                </div>
                <div className="stat-info">
                  <span className="stat-number">
                    {safeOverall.wrong_answers}
                  </span>
                  <span className="stat-label">Incorrect</span>
                </div>
              </div>

              <div className="stat-card unanswered">
                <div className="stat-icon">
                  <FaClock />
                </div>
                <div className="stat-info">
                  <span className="stat-number">
                    {safeOverall.unanswered_questions}
                  </span>
                  <span className="stat-label">Unanswered</span>
                </div>
              </div>

              <div className="stat-card time">
                <div className="stat-icon">
                  <FaChartBar />
                </div>
                <div className="stat-info">
                  <span className="stat-number">
                    {formatNumber(safeOverall.average_time_per_question)}m
                  </span>
                  <span className="stat-label">Avg Time/Q</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="detailed-analysis">
            <div className="analysis-tabs">
              <button
                className={`tab-btn ${
                  activeTab === "overview" ? "active" : ""
                }`}
                onClick={() => setActiveTab("overview")}
              >
                <FaListAlt className="me-2" />
                Question Analysis
              </button>
              <button
                className={`tab-btn ${
                  activeTab === "sections" ? "active" : ""
                }`}
                onClick={() => setActiveTab("sections")}
              >
                <FaBook className="me-2" />
                Section Analysis
              </button>
              {/* <button 
              className={`tab-btn ${activeTab === "recommendations" ? "active" : ""}`}
              onClick={() => setActiveTab("recommendations")}
            >
              <FaLightbulb className="me-2" />
              Recommendations
            </button> */}
            </div>

            <div className="tab-content">
              {/* Question Analysis Tab */}
              {activeTab === "overview" && (
                <div className="questions-analysis">
                  <div className="section-sidebar">
                    <h4>Sections</h4>
                    <div className="section-list">
                      {sections?.map((section, index) => {
                        const questionRange = getSectionQuestionRange(index);
                        const sectionStats = section.section_stats || {};

                        return (
                          <button
                            key={section.section_id}
                            className={`section-item ${
                              selectedSection?.section_id === section.section_id
                                ? "active"
                                : ""
                            }`}
                            onClick={() => setSelectedSection(section)}
                          >
                            <div className="section-info">
                              <span className="section-name">
                                {section.section_title}
                              </span>
                              <span className="section-range">
                                Questions {questionRange}
                              </span>
                            </div>
                            <div className="section-stats">
                              {/* <span className="correct-count">
                              {sectionStats.correct_answers || 0}
                            </span> */}
                              <span className="total-count">
                                {sectionStats.total_questions || 0}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="questions-list">
                    {selectedSection &&
                      selectedSection.passages?.map((passage, pIndex) => (
                        <div key={passage.passage_id} className="passage-block">
                          {passage.passage_title && (
                            <div className="passage-header">
                              <h5>{passage.passage_title}</h5>
                              {passage.passage_content && (
                                <div className="passage-content-full">
                                  <div className="passage-label">
                                    Passage Content:
                                  </div>
                                  <div className="passage-text">
                                    {passage.passage_content}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="questions-grid">
                            {passage.questions?.map((question, qIndex) => {
                              const safeAttempt = question.user_attempt || {};
                              return (
                                <div
                                  key={question.question_id}
                                  className={`question-card ${
                                    safeAttempt.is_correct
                                      ? "correct"
                                      : "incorrect"
                                  } ${
                                    !safeAttempt.user_answer
                                      ? "unattempted"
                                      : ""
                                  }`}
                                >
                                  <div className="question-header">
                                    <span className="question-number">
                                      Q{question.question_number}
                                    </span>
                                    <div className="question-status">
                                      {safeAttempt.user_answer ? (
                                        safeAttempt.is_correct ? (
                                          <span className="status correct">
                                            <FaCheck className="me-1" />
                                            Correct
                                          </span>
                                        ) : (
                                          <span className="status incorrect">
                                            <FaTimes className="me-1" />
                                            Incorrect
                                          </span>
                                        )
                                      ) : (
                                        <span className="status unattempted">
                                          <FaClock className="me-1" />
                                          Unattempted
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="question-content">
                                    <p className="question-text">
                                      {question.question_text}
                                    </p>

                                    {question.options &&
                                      question.options.length > 0 && (
                                        <div className="options-review">
                                          <div className="options-list">
                                            {question.options.map(
                                              (option, optIndex) => {
                                                const optionLetter =
                                                  String.fromCharCode(
                                                    65 + optIndex
                                                  );
                                                const isUserAnswer =
                                                  safeAttempt.user_answer ===
                                                  optionLetter;
                                                const isCorrectAnswer =
                                                  question.correct_answer ===
                                                  optionLetter;

                                                return (
                                                  <div
                                                    key={optIndex}
                                                    className={`option-item ${
                                                      isUserAnswer
                                                        ? "user-answer"
                                                        : ""
                                                    } ${
                                                      isCorrectAnswer
                                                        ? "correct-answer"
                                                        : ""
                                                    }`}
                                                  >
                                                    <span className="option-letter">
                                                      {optionLetter}.
                                                    </span>
                                                    <span className="option-text">
                                                      {option}
                                                    </span>
                                                    {isUserAnswer &&
                                                      !isCorrectAnswer && (
                                                        <FaTimes className="wrong-indicator" />
                                                      )}
                                                    {isCorrectAnswer && (
                                                      <FaCheck className="correct-indicator" />
                                                    )}
                                                  </div>
                                                );
                                              }
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    <div className="answer-analysis">
                                      <div className="answer-details">
                                        <span className="detail-item">
                                          <strong>Your Answer:</strong>
                                          {safeAttempt.user_answer
                                            ? ` ${safeAttempt.user_answer}`
                                            : " Not Attempted"}
                                        </span>
                                        <span className="detail-item">
                                          <strong>Correct Answer:</strong>{" "}
                                          {question.correct_answer || "N/A"}
                                        </span>
                                        <span className="detail-item">
                                          <strong>Marks:</strong>{" "}
                                          {safeAttempt.marks_obtained || 0}/
                                          {question.marks || 0}
                                        </span>
                                        <span className="detail-item">
                                          <strong>Time:</strong>{" "}
                                          {formatNumber(safeAttempt.time_spent)}
                                          m
                                        </span>
                                      </div>

                                      {question.explanation && (
                                        <div className="explanation">
                                          <strong>Explanation:</strong>
                                          <p>{question.explanation}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Section Analysis Tab */}
              {activeTab === "sections" && (
                <div className="section-analysis">
                  <h3>Section-wise Performance</h3>
                  <div className="section-performance-grid">
                    {section_wise_summary?.map((section, index) => {
                      const safeSection = section || {};
                      const accuracy = parseFloat(safeSection.accuracy) || 0;
                      const correctAnswers = safeSection.correct_answers || 0;
                      const totalQuestions = safeSection.total_questions || 0;
                      const marksObtained = safeSection.marks_obtained || 0;
                      const timeSpent = safeSection.time_spent || 0;
                      const questionRange =
                        safeSection.question_range ||
                        getSectionQuestionRange(index);

                      return (
                        <div key={index} className="section-performance-card">
                          <div className="section-header">
                            <h4>
                              {safeSection.section_title ||
                                `Section ${index + 1}`}
                            </h4>
                            <span className="section-type">
                              {safeSection.section_type || "General"}
                            </span>
                          </div>

                          <div className="question-range-badge">
                            Questions {questionRange}
                          </div>

                          <div className="performance-metrics">
                            <div className="metric">
                              <span className="metric-value">
                                {correctAnswers}/{totalQuestions}
                              </span>
                              <span className="metric-label">Correct</span>
                            </div>
                            <div className="metric">
                              <span className="metric-value">
                                {formatPercentage(accuracy)}%
                              </span>
                              <span className="metric-label">Accuracy</span>
                            </div>
                            <div className="metric">
                              <span className="metric-value">
                                {formatNumber(marksObtained)}
                              </span>
                              <span className="metric-label">Marks</span>
                            </div>
                            <div className="metric">
                              <span className="metric-value">
                                {formatNumber(timeSpent)}m
                              </span>
                              <span className="metric-label">Time</span>
                            </div>
                          </div>

                          <div className="accuracy-container">
                            <div className="accuracy-label">
                              <span>
                                Accuracy: {formatPercentage(accuracy)}%
                              </span>
                            </div>
                            <div className="accuracy-bar">
                              <div
                                className="accuracy-fill"
                                style={{
                                  width: `${Math.min(accuracy, 100)}%`,
                                  backgroundColor:
                                    getPerformanceColor(accuracy),
                                }}
                              ></div>
                            </div>
                          </div>

                          <div className="section-details">
                            <div className="detail-row">
                              <span>Wrong Answers:</span>
                              <span>{safeSection.wrong_answers || 0}</span>
                            </div>
                            <div className="detail-row">
                              <span>Time per Question:</span>
                              <span>
                                {totalQuestions > 0
                                  ? formatNumber(timeSpent / totalQuestions)
                                  : 0}
                                m
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {section_wise_summary && section_wise_summary.length > 0 && (
                    <div className="overall-section-summary">
                      <h4>Section Performance Overview</h4>
                      <div className="summary-stats">
                        <div className="summary-stat">
                          <span className="stat-value">
                            {section_wise_summary.length}
                          </span>
                          <span className="stat-label">Total Sections</span>
                        </div>
                        <div className="summary-stat">
                          <span className="stat-value">
                            {formatPercentage(
                              section_wise_summary.reduce(
                                (sum, section) =>
                                  sum + (parseFloat(section.accuracy) || 0),
                                0
                              ) / section_wise_summary.length
                            )}
                            %
                          </span>
                          <span className="stat-label">Average Accuracy</span>
                        </div>
                        <div className="summary-stat">
                          <span className="stat-value">
                            {formatNumber(
                              section_wise_summary.reduce(
                                (sum, section) =>
                                  sum +
                                  (parseFloat(section.marks_obtained) || 0),
                                0
                              )
                            )}
                          </span>
                          <span className="stat-label">Total Marks</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recommendations Tab */}
              {activeTab === "recommendations" && (
                <div className="recommendations-tab">
                  <h3>Personalized Recommendations</h3>
                  <div className="recommendations-grid">
                    <div className="recommendation-card primary">
                      <FaLightbulb className="rec-icon" />
                      <h4>Focus Areas</h4>
                      <ul>
                        {(recommendations || [])
                          .slice(0, 3)
                          .map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                      </ul>
                    </div>

                    <div className="recommendation-card secondary">
                      <FaBook className="rec-icon" />
                      <h4>Study Plan</h4>
                      <ul>
                        <li>Review incorrect answers thoroughly</li>
                        <li>Practice similar question patterns</li>
                        <li>Focus on time management</li>
                        {safeOverall.percentage < 60 && (
                          <li>Take more sectional tests</li>
                        )}
                      </ul>
                    </div>

                    <div className="recommendation-card success">
                      <FaTrophy className="rec-icon" />
                      <h4>Next Steps</h4>
                      <ul>
                        <li>Retake this test after revision</li>
                        <li>Try advanced level tests</li>
                        <li>Join discussion forums</li>
                        <li>Set daily practice goals</li>
                      </ul>
                    </div>
                  </div>

                  <div className="improvement-tips">
                    <h4>Quick Improvement Tips</h4>
                    <div className="tips-grid">
                      <div className="tip-item">
                        <span className="tip-number">01</span>
                        <p>Focus on sections with lowest accuracy</p>
                      </div>
                      <div className="tip-item">
                        <span className="tip-number">02</span>
                        <p>Practice time-bound tests regularly</p>
                      </div>
                      <div className="tip-item">
                        <span className="tip-number">03</span>
                        <p>Analyze mistakes and learn from them</p>
                      </div>
                      <div className="tip-item">
                        <span className="tip-number">04</span>
                        <p>Revise fundamental concepts</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="result-actions">
            <Link to="/test-series" className="educate-btn">
              <FaRedo className="me-2" />
              Take Another Test
            </Link>
            <button onClick={fetchExamResult} className="btn secondary">
              <FaChartBar className="me-2" />
              Refresh Results
            </button>
            <Link to="/dashboard" className="educate-btn bg-primary">
              <FaArrowLeft className="me-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ExamResult;
